import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { formatINR } from '../utils/priceFormatter';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ProductModal from './ProductModal';
import shoe1 from './images/shoe1.png';
import shoe2 from './images/shoe2.png';
import shoe5 from './images/shoe5.png';
import shoe6 from './images/shoe6.png';
import shoe7 from './images/shoe7.png';
import shoe8 from './images/shoe8.png';
import shoe9 from './images/shoe9.png';
import sh1 from './images/sh1.png';
import sh2 from './images/sh2.png';
import sh3 from './images/sh3.png';

gsap.registerPlugin(ScrollTrigger);

const FeaturedProducts = () => {
  const sectionRef = useRef();
  const titleRef = useRef();
  const carouselRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productsInCart, setProductsInCart] = useState(new Set());
  const navigate = useNavigate();
  const { cartItems } = useCart();

  const featuredProducts = [
    {
      id: 1,
      name: "Air Max 270",
      category: "Lifestyle",
      price: 12450, // 150 USD * 83
      image: shoe1,
      description: "Maximum air, maximum style. The Nike Air Max 270 delivers visible Air cushioning from heel to toe.",
      badge: "BESTSELLER",
      colors: ["Black", "White", "Red"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.8,
      reviews: 1250
    },
    {
      id: 2,
      name: "Air Force 1",
      category: "Classic",
      price: 9130, // 110 USD * 83
      image: shoe2,
      description: "Timeless basketball icon with premium leather and classic styling that never goes out of fashion.",
      badge: "CLASSIC",
      colors: ["White", "Black", "Navy"],
      sizes: [6, 7, 8, 9, 10, 11, 12, 13],
      rating: 4.9,
      reviews: 2100
    },
    {
      id: 3,
      name: "React Infinity",
      category: "Running",
      price: 13280, // 160 USD * 83
      image: shoe5,
      description: "Engineered for comfort and support during long-distance runs with React foam technology.",
      badge: "NEW",
      colors: ["Blue", "Black", "Gray"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.6,
      reviews: 890
    },
    {
      id: 4,
      name: "ZoomX Vaporfly",
      category: "Performance",
      price: 20750, // 250 USD * 83
      image: shoe6,
      description: "Built for speed with ZoomX foam and carbon fiber plate for maximum energy return during racing.",
      badge: "PREMIUM",
      colors: ["Volt", "Black", "Pink"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.7,
      reviews: 680
    },
    {
      id: 5,
      name: "Blazer Mid",
      category: "Lifestyle",
      price: 8300, // 100 USD * 83
      image: shoe7,
      description: "Retro basketball style meets modern street fashion with premium leather construction.",
      badge: "TRENDING",
      colors: ["White", "Black", "Red", "Navy"],
      sizes: [6, 7, 8, 9, 10, 11, 12, 13],
      rating: 4.5,
      reviews: 1420
    },
    {
      id: 6,
      name: "Dunk Low",
      category: "Street",
      price: 9130, // 110 USD * 83
      image: shoe8,
      description: "Street style icon with classic basketball heritage and versatile colorways.",
      badge: "LIMITED",
      colors: ["White/Black", "University Blue", "Chicago"],
      sizes: [6, 7, 8, 9, 10, 11, 12, 13],
      rating: 4.8,
      reviews: 2340
    },
    {
      id: 7,
      name: "Air Jordan 1",
      category: "Basketball",
      price: 14110, // 170 USD * 83
      image: shoe9,
      description: "Legendary basketball heritage with iconic design that revolutionized sneaker culture.",
      badge: "ICONIC",
      colors: ["Bred", "Royal", "Shadow", "Chicago"],
      sizes: [7, 8, 9, 10, 11, 12, 13, 14],
      rating: 4.9,
      reviews: 3200
    },
    {
      id: 8,
      name: "Air Max 90",
      category: "Lifestyle",
      price: 9960, // 120 USD * 83
      image: sh1,
      description: "Classic comfort meets style with visible Air cushioning and premium materials.",
      badge: "CLASSIC",
      colors: ["White", "Black", "Grey", "Red"],
      sizes: [6, 7, 8, 9, 10, 11, 12, 13],
      rating: 4.6,
      reviews: 1890
    },
    {
      id: 9,
      name: "React Element",
      category: "Running",
      price: 130,
      image: sh2,
      description: "Innovative cushioning technology with React foam for responsive comfort and style.",
      badge: "TECH",
      colors: ["Black", "White", "Blue", "Orange"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.4,
      reviews: 760
    },
    {
      id: 10,
      name: "Air Max Plus",
      category: "Lifestyle",
      price: 140,
      image: sh3,
      description: "Bold design with maximum impact featuring distinctive wave-inspired lines and Air Max cushioning.",
      badge: "BOLD",
      colors: ["Black", "White", "Volt", "Sunset"],
      sizes: [7, 8, 9, 10, 11, 12, 13],
      rating: 4.3,
      reviews: 950
    }
  ];

  const totalProducts = featuredProducts.length;
  const duplicatedProducts = [...featuredProducts, ...featuredProducts, ...featuredProducts];

  useEffect(() => {
    // Title animation
    gsap.fromTo(titleRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Carousel container animation
    gsap.fromTo(carouselRef.current,
      { y: 80, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.2,
        delay: 0.3,
        scrollTrigger: {
          trigger: carouselRef.current,
          start: "top 85%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Handle infinite loop reset
  useEffect(() => {
    if (!carouselRef.current) return;

    if (currentIndex >= totalProducts * 2) {
      // Reset to the beginning of second set without animation
      const timer = setTimeout(() => {
        if (carouselRef.current) {
          carouselRef.current.style.transition = 'none';
          setCurrentIndex(totalProducts);
          requestAnimationFrame(() => {
            if (carouselRef.current) {
              carouselRef.current.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
          });
        }
      }, 50);
      return () => clearTimeout(timer);
    } else if (currentIndex < totalProducts) {
      // Reset to the end of second set without animation
      const timer = setTimeout(() => {
        if (carouselRef.current) {
          carouselRef.current.style.transition = 'none';
          setCurrentIndex(totalProducts * 2 - 1);
          requestAnimationFrame(() => {
            if (carouselRef.current) {
              carouselRef.current.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
          });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, totalProducts]);

  // Initialize at the middle set
  useEffect(() => {
    setCurrentIndex(totalProducts);
  }, [totalProducts]);

  // Track products in cart
  useEffect(() => {
    const productIds = new Set(cartItems.map(item => item.id));
    setProductsInCart(productIds);
  }, [cartItems]);

  const goToSlide = (index) => {
    setCurrentIndex(totalProducts + index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => prev + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => prev - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const openModal = (product, focusOnSize = false) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    setIsAutoPlaying(false);
    
    // If focusOnSize is true, we'll pass this info to ProductModal
    if (focusOnSize) {
      setSelectedProduct({ ...product, focusOnSize: true });
    }
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setIsModalOpen(false);
    setIsAutoPlaying(true);
  };

  const handleAddToCart = (cartItem) => {
    console.log('Added to cart:', cartItem);
    // Here you would typically add to cart state or send to backend
    alert(`Added ${cartItem.product.name} (Size: ${cartItem.size}, Color: ${cartItem.color}, Qty: ${cartItem.quantity}) to cart!`);
  };

  const handleViewAllProducts = () => {
    // Navigate to store page
    navigate('/store');
  };

  const handleCartAction = (product) => {
    if (productsInCart.has(product.id)) {
      // Product is in cart, go to cart page
      navigate('/cart');
    } else {
      // Product not in cart, open modal for adding
      openModal(product, true);
    }
  };

  const getTransform = () => {
    const isMobile = windowWidth <= 480;
    const isTablet = windowWidth <= 768 && windowWidth > 480;
    
    if (isMobile) {
      // 260px card + 10px gap = 270px movement
      return `translateX(calc(50% - 130px - ${currentIndex * 270}px))`;
    } else if (isTablet) {
      // 280px card + 15px gap = 295px movement
      return `translateX(calc(50% - 140px - ${currentIndex * 295}px))`;
    } else {
      // 320px card + 20px gap = 340px movement
      return `translateX(calc(50% - 160px - ${currentIndex * 340}px))`;
    }
  };

  return (
    <Section ref={sectionRef} data-section="featured-products">
      <Container>
        <SectionHeader>
          <SectionTitle ref={titleRef}>Featured Products</SectionTitle>
          <SectionSubtitle>Discover our most popular Nike collections</SectionSubtitle>
        </SectionHeader>
        
        <CarouselContainer ref={carouselRef}>
          <CarouselTrack 
            style={{ 
              transform: getTransform(),
              transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {duplicatedProducts.map((product, index) => (
              <ProductCard 
                key={`${product.id}-${index}`}
                onClick={() => openModal(product)}
              >
                <ProductBadge>{product.badge}</ProductBadge>
                <ProductImage>
                  <img src={product.image} alt={product.name} />
                  <ProductOverlay>
                    <QuickViewButton onClick={(e) => {
                      e.stopPropagation();
                      openModal(product);
                    }}>
                      Quick View
                    </QuickViewButton>
                    <AddToFavButton onClick={(e) => e.stopPropagation()}>♡</AddToFavButton>
                  </ProductOverlay>
                </ProductImage>
                
                <ProductInfo>
                  <ProductCategory>{product.category}</ProductCategory>
                  <ProductName>{product.name}</ProductName>
                  <ProductDescription>{product.description}</ProductDescription>
                  <ProductPrice>{formatINR(product.price)}</ProductPrice>
                  
                  <ProductActions>
                    <AddToCartButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCartAction(product);
                      }}
                      isInCart={productsInCart.has(product.id)}
                    >
                      {productsInCart.has(product.id) ? (
                        <>
                          <CartIconSmall>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="9" cy="21" r="1"></circle>
                              <circle cx="20" cy="21" r="1"></circle>
                              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                            </svg>
                          </CartIconSmall>
                          Go to Cart
                        </>
                      ) : (
                        'Add to Cart'
                      )}
                    </AddToCartButton>
                  </ProductActions>
                </ProductInfo>
              </ProductCard>
            ))}
          </CarouselTrack>
          
          <CarouselButton 
            className="prev" 
            onClick={prevSlide}
            aria-label="Previous products"
          >
            ‹
          </CarouselButton>
          
          <CarouselButton 
            className="next" 
            onClick={nextSlide}
            aria-label="Next products"
          >
            ›
          </CarouselButton>
        </CarouselContainer>
        
        <CarouselDots>
          {featuredProducts.map((_, index) => (
            <Dot
              key={index}
              active={index === (currentIndex % totalProducts)}
              onClick={() => goToSlide(index)}
            />
          ))}
        </CarouselDots>
        
        <ViewAllButton onClick={handleViewAllProducts}>View All Products</ViewAllButton>
      </Container>
      
      {/* Product Modal */}
      <ProductModal
        isOpen={isModalOpen}
        product={selectedProduct}
        onClose={closeModal}
        onAddToCart={handleAddToCart}
      />
    </Section>
  );
};

export default FeaturedProducts;

const Section = styled.section`
  padding: 100px 0;
  background: #f8f8f8;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-family: 'Anton', sans-serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: #111;
  margin-bottom: 16px;
  letter-spacing: 2px;
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 16px;
  background: #f8f8f8;
  padding: 30px 0;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    padding: 20px 0;
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    gap: 15px;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const ProductCard = styled.div`
  flex: 0 0 320px;
  background: #fff;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 8px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    border-color: #e0e0e0;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    
    img {
      transform: scale(1.05);
    }
  }
  
  @media (max-width: 768px) {
    flex: 0 0 280px;
    min-height: 420px;
  }
  
  @media (max-width: 480px) {
    flex: 0 0 260px;
    min-height: 380px;
  }
`;

const ProductBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 3;
  border-radius: 12px;
  backdrop-filter: blur(4px);
`;

const ProductImage = styled.div`
  position: relative;
  height: 280px;
  overflow: hidden;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 90%;
    height: 90%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  @media (max-width: 768px) {
    height: 240px;
    
    img {
      width: 85%;
      height: 85%;
    }
  }
  
  @media (max-width: 480px) {
    height: 200px;
  }
`;

const ProductOverlay = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: all 0.3s ease;
  transform: translateY(10px);
  
  ${ProductCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const QuickViewButton = styled.button`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  padding: 8px 12px;
  font-weight: 500;
  cursor: pointer;
  font-size: 11px;
  border-radius: 20px;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-1px);
  }
`;

const AddToFavButton = styled.button`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-1px);
  }
`;

const ProductInfo = styled.div`
  padding: 16px 18px 18px;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 14px 16px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px 14px;
  }
`;

const ProductCategory = styled.span`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
  margin-bottom: 6px;
  display: block;
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #111;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const ProductDescription = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.4;
  flex: 1;
`;

const ProductPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin: 0 0 16px 0;
  
  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

const ProductActions = styled.div`
  margin-top: auto;
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: ${props => props.isInCart ? '#2980b9' : '#111'};
  color: white;
  border: none;
  padding: 12px 16px;
  font-weight: 500;
  cursor: pointer;
  font-size: 13px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.isInCart ? '#1f5582' : '#000'};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 12px;
  }
`;

const CartIconSmall = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    transition: transform 0.3s ease;
  }
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: #111;
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 20px;
  }
  
  &.next {
    right: 20px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
    
    &.prev {
      left: 10px;
    }
    
    &.next {
      right: 10px;
    }
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 40px;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#111' : '#ddd'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#111' : '#999'};
    transform: scale(1.2);
  }
`;

const ViewAllButton = styled.button`
  display: block;
  margin: 0 auto;
  background: transparent;
  color: #111;
  border: 2px solid #111;
  padding: 16px 32px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  border-radius: 0;
  
  &:hover {
    background: #111;
    color: white;
    transform: translateY(-2px);
  }
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  animation: slideUp 0.3s ease;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  
  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }
`;

const ModalNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 16px;
  }
  
  &.next {
    right: 16px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
    
    &.prev {
      left: 12px;
    }
    
    &.next {
      right: 12px;
    }
  }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 500px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const ModalImageSection = styled.div`
  position: relative;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
    min-height: 300px;
  }
`;

const ModalImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 400px;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    img {
      max-height: 250px;
    }
  }
`;

const ModalBadge = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 16px;
  backdrop-filter: blur(4px);
`;

const ModalInfoSection = styled.div`
  padding: 40px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const ModalCategory = styled.span`
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: #111;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ModalDescription = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const ModalPrice = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: #111;
  margin: 0 0 30px 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ModalFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
`;

const FeatureItem = styled.li`
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  
  &:before {
    content: "";
    width: 4px;
    height: 4px;
    background: #111;
    border-radius: 50%;
    margin-right: 12px;
    display: none;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: auto;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ModalAddToCart = styled.button`
  flex: 1;
  background: #111;
  color: white;
  border: none;
  padding: 16px 24px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: #000;
    transform: translateY(-1px);
  }
`;

const ModalWishlist = styled.button`
  background: transparent;
  color: #111;
  border: 2px solid #111;
  padding: 16px 24px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #111;
    color: white;
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    text-align: center;
  }
`;
